<template>
  <v-main>
    <v-container fluid>
      <!-- Unified Upload Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl">
            <!-- Page Header Section -->
            <!-- Page Header -->
            <page-header
              title="Bulk Seller Product Variant Upload"
              subtitle="Upload multiple seller product variants using Excel file with validation and preview"
            >
              <template v-slot:actions>
                <div class="d-flex justify-end align-center">
                    <v-btn
                      color="info"
                      elevation="1"
                      small
                      @click="downloadTemplate"
                      :loading="isDownloadingTemplate"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-download</v-icon>
                      Download Template
                    </v-btn>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      small
                      @click="$router.back()"
                      class="px-3"
                    >
                      <v-icon left x-small>mdi-arrow-left</v-icon>
                      Back
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Section Divider -->
            <v-divider class="mx-4"></v-divider>

            <!-- Upload Steps Section -->
            <v-card-text class="pa-4 pb-2">
              <v-stepper v-model="currentStep" non-linear>
                <v-stepper-header>
                  <v-stepper-step step="1" :complete="currentStep > 1" editable>
                    Upload File
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step step="2" :complete="currentStep > 2" :editable="hasPreviewData">
                    Preview & Validate
                  </v-stepper-step>
                  <v-divider></v-divider>
                  <v-stepper-step step="3" :complete="currentStep > 3" :editable="canConfirm">
                    Confirm & Save
                  </v-stepper-step>
                </v-stepper-header>
              </v-stepper>
            </v-card-text>

            <!-- Section Divider -->
            <v-divider class="mx-4"></v-divider>
            <!-- Step 1: File Upload -->
            <div v-show="currentStep === 1">
              <v-card-text class="pa-4 pt-2">
                <v-row>
                  <!-- File Upload Area -->
                  <v-col cols="12" md="8">
                    <v-file-input
                      v-model="selectedFile"
                      label="Select Excel File"
                      placeholder="Choose .xlsx or .xls file"
                      prepend-icon="mdi-file-excel"
                      accept=".xlsx,.xls"
                      show-size
                      :rules="fileRules"
                      :error-messages="fileErrors"
                      @change="handleFileSelection"
                      class="mb-4"
                    ></v-file-input>

                    <v-textarea
                      v-model="uploadNotes"
                      label="Upload Notes (Optional)"
                      placeholder="Add any notes about this upload..."
                      rows="3"
                      outlined
                      hide-details
                      class="mb-4"
                    ></v-textarea>

                    <div class="d-flex justify-end">
                      <v-btn
                        color="primary"
                        :disabled="!selectedFile || isUploading"
                        :loading="isUploading"
                        @click="uploadAndPreview"
                        class="px-6"
                      >
                        <v-icon left>mdi-cloud-upload</v-icon>
                        Upload & Preview
                      </v-btn>
                    </div>
                  </v-col>

                  <!-- Instructions -->
                  <v-col cols="12" md="4">
                    <v-card outlined>
                      <v-card-title class="pb-2">
                        <v-icon color="info" class="mr-2">mdi-information</v-icon>
                        Upload Instructions
                      </v-card-title>
                      <v-card-text>
                        <v-list dense>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="success">mdi-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Download template first
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="success">mdi-check</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Fill required fields (marked with *)
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="info">mdi-layers</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Creates products, variants, sellers automatically
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="info">mdi-account-multiple</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Auto-increments stock for existing variants
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="info">mdi-file</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Max file size: 10MB
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="primary">mdi-image-multiple</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Include image URLs (first image mandatory)
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-list-item>
                            <v-list-item-icon>
                              <v-icon small color="warning">mdi-download</v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title class="text-caption">
                                Images downloaded automatically from URLs
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </v-list>
                      </v-card-text>
                    </v-card>
                    
                    <!-- Reference Data -->
                    <v-card outlined class="mt-3">
                      <v-card-title class="pb-2">
                        <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                        Reference Data
                        <v-spacer></v-spacer>
                        <v-btn 
                          v-if="isLoadingMasters"
                          icon 
                          small 
                          :loading="true"
                        ></v-btn>
                      </v-card-title>
                      <v-card-text class="pa-2">
                        <v-expansion-panels accordion multiple>
                          <!-- Categories -->
                          <v-expansion-panel>
                            <v-expansion-panel-header>
                              <div class="d-flex align-center">
                                <v-icon color="green" class="mr-2" small>mdi-tag</v-icon>
                                <span class="text-body-2 font-weight-medium">Categories</span>
                                <v-chip x-small class="ml-2">{{ categories.length }}</v-chip>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="text-caption grey--text mb-2">
                                Available category names for products. Click to copy:
                              </div>
                              <v-chip-group column>
                                <v-tooltip bottom v-for="category in categories" :key="category.id">
                                  <template #activator="{ on, attrs }">
                                    <v-chip 
                                      v-bind="attrs"
                                      v-on="on"
                                      small
                                      outlined
                                      color="green"
                                      class="chip-clickable"
                                      @click="copyToClipboard(category.name)"
                                    >
                                      <v-icon left x-small>mdi-content-copy</v-icon>
                                      {{ category.name }}
                                    </v-chip>
                                  </template>
                                  <span>Click to copy: {{ category.name }}</span>
                                </v-tooltip>
                              </v-chip-group>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                          
                          <!-- Units -->
                          <v-expansion-panel>
                            <v-expansion-panel-header>
                              <div class="d-flex align-center">
                                <v-icon color="blue" class="mr-2" small>mdi-weight</v-icon>
                                <span class="text-body-2 font-weight-medium">Product Units</span>
                                <v-chip x-small class="ml-2">{{ units.length }}</v-chip>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="text-caption grey--text mb-2">
                                Available unit names for product variants. Click to copy:
                              </div>
                              <v-chip-group column>
                                <v-tooltip bottom v-for="unit in units" :key="unit.id">
                                  <template #activator="{ on, attrs }">
                                    <v-chip 
                                      v-bind="attrs"
                                      v-on="on"
                                      small
                                      outlined
                                      color="blue"
                                      class="chip-clickable"
                                      @click="copyToClipboard(unit.name)"
                                    >
                                      <v-icon left x-small>mdi-content-copy</v-icon>
                                      {{ unit.name }}
                                    </v-chip>
                                  </template>
                                  <span>Click to copy: {{ unit.name }}</span>
                                </v-tooltip>
                              </v-chip-group>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                          
                          <!-- Packing Types -->
                          <v-expansion-panel>
                            <v-expansion-panel-header>
                              <div class="d-flex align-center">
                                <v-icon color="orange" class="mr-2" small>mdi-package-variant</v-icon>
                                <span class="text-body-2 font-weight-medium">Packing Types</span>
                                <v-chip x-small class="ml-2">{{ packingTypes.length }}</v-chip>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="text-caption grey--text mb-2">
                                Available packing type names for product variants. Click to copy:
                              </div>
                              <v-chip-group column>
                                <v-tooltip bottom v-for="packingType in packingTypes" :key="packingType.id">
                                  <template #activator="{ on, attrs }">
                                    <v-chip
                                      v-bind="attrs"
                                      v-on="on"
                                      small
                                      outlined
                                      color="orange"
                                      class="chip-clickable"
                                      @click="copyToClipboard(packingType.name)"
                                    >
                                      <v-icon left x-small>mdi-content-copy</v-icon>
                                      {{ packingType.name }}
                                    </v-chip>
                                  </template>
                                  <span>Click to copy: {{ packingType.name }}</span>
                                </v-tooltip>
                              </v-chip-group>
                            </v-expansion-panel-content>
                          </v-expansion-panel>

                          <!-- Tags -->
                          <v-expansion-panel>
                            <v-expansion-panel-header>
                              <div class="d-flex align-center">
                                <v-icon color="purple" class="mr-2" small>mdi-label</v-icon>
                                <span class="text-body-2 font-weight-medium">Tags</span>
                                <v-chip x-small class="ml-2">{{ tags.length }}</v-chip>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="text-caption grey--text mb-2">
                                Available tag names for products. Click to copy:
                              </div>
                              <v-chip-group column>
                                <v-tooltip bottom v-for="tag in tags" :key="tag.id">
                                  <template #activator="{ on, attrs }">
                                    <v-chip
                                      v-bind="attrs"
                                      v-on="on"
                                      small
                                      outlined
                                      color="purple"
                                      class="chip-clickable"
                                      @click="copyToClipboard(tag.name)"
                                    >
                                      <v-icon left x-small>mdi-content-copy</v-icon>
                                      {{ tag.name }}
                                    </v-chip>
                                  </template>
                                  <span>Click to copy: {{ tag.name }}</span>
                                </v-tooltip>
                              </v-chip-group>
                            </v-expansion-panel-content>
                          </v-expansion-panel>

                          <!-- Positions -->
                          <v-expansion-panel>
                            <v-expansion-panel-header>
                              <div class="d-flex align-center">
                                <v-icon color="deep-purple" class="mr-2" small>mdi-account-eye</v-icon>
                                <span class="text-body-2 font-weight-medium">Positions</span>
                                <v-chip x-small class="ml-2">{{ positions.length }}</v-chip>
                              </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content>
                              <div class="text-caption grey--text mb-2">
                                Use in Position column to restrict visibility. Leave blank = visible to all. Click to copy:
                              </div>
                              <v-chip-group column>
                                <v-tooltip bottom v-for="pos in positions" :key="pos.id">
                                  <template #activator="{ on, attrs }">
                                    <v-chip
                                      v-bind="attrs"
                                      v-on="on"
                                      small
                                      outlined
                                      color="deep-purple"
                                      class="chip-clickable"
                                      @click="copyToClipboard(pos.name)"
                                    >
                                      <v-icon left x-small>mdi-content-copy</v-icon>
                                      {{ pos.name }}
                                    </v-chip>
                                  </template>
                                  <span>Click to copy: {{ pos.name }}</span>
                                </v-tooltip>
                              </v-chip-group>
                              <div class="text-caption mt-2 info--text">
                                <v-icon x-small color="info">mdi-information</v-icon>
                                Doctor position: consumer_discount not required.<br>
                                Patient position: doctor_discount not required.
                              </div>
                            </v-expansion-panel-content>
                          </v-expansion-panel>
                        </v-expansion-panels>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>

            <!-- Section Divider -->
            <v-divider v-if="currentStep > 1" class="mx-4 my-2"></v-divider>

            <!-- Step 2: Preview & Validation -->
            <div v-show="currentStep === 2 && hasPreviewData">
              <v-card-text class="pa-4 pt-2">
                <!-- Validation Summary -->
                <v-row class="mb-4">
                  <v-col :cols="validationSummary.has_skipped ? 12 : 12" :md="validationSummary.has_skipped ? '2' : '3'">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="primary">mdi-file-table</v-icon>
                        <div class="text-h6 font-weight-bold">{{ validationSummary.total_rows }}</div>
                        <div class="text-caption">Processed Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col :cols="validationSummary.has_skipped ? 12 : 12" :md="validationSummary.has_skipped ? '2' : '3'">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="success">mdi-check-circle</v-icon>
                        <div class="text-h6 font-weight-bold text-success">{{ validationSummary.valid_rows }}</div>
                        <div class="text-caption">Valid Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col :cols="validationSummary.has_skipped ? 12 : 12" :md="validationSummary.has_skipped ? '2' : '3'">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="error">mdi-alert-circle</v-icon>
                        <div class="text-h6 font-weight-bold text-error">{{ validationSummary.invalid_rows }}</div>
                        <div class="text-caption">Invalid Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col v-if="validationSummary.has_skipped" cols="12" md="2">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" color="grey">mdi-minus-circle</v-icon>
                        <div class="text-h6 font-weight-bold text-grey">{{ validationSummary.skipped_rows || 0 }}</div>
                        <div class="text-caption">Skipped Rows</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col :cols="validationSummary.has_skipped ? 12 : 12" :md="validationSummary.has_skipped ? '4' : '3'">
                    <v-card outlined>
                      <v-card-text class="text-center pa-3">
                        <v-icon size="32" :color="validationSummary.success_rate >= 80 ? 'success' : validationSummary.success_rate >= 60 ? 'warning' : 'error'">
                          mdi-chart-line
                        </v-icon>
                        <div class="text-h6 font-weight-bold" :class="validationSummary.success_rate >= 80 ? 'text-success' : validationSummary.success_rate >= 60 ? 'text-warning' : 'text-error'">
                          {{ validationSummary.success_rate }}%
                        </div>
                        <div class="text-caption">Success Rate</div>
                        <div v-if="validationSummary.has_skipped" class="text-caption grey--text">
                          ({{ validationSummary.total_file_rows || 0 }} total in file)
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Preview Data Table -->
                <v-card outlined class="mb-4">
                  <v-card-title class="pb-2">
                    <v-icon color="primary" class="mr-2">mdi-table</v-icon>
                    Data Preview - All Template Fields (First 20 rows)
                  </v-card-title>
                  <v-data-table
                    :headers="previewHeaders"
                    :items="previewData"
                    :items-per-page="10"
                    class="elevation-0 preview-table"
                    fixed-header
                    height="450"
                    dense
                  >
                    <!-- Row Number -->
                    <template #[`item.row_number`]="{ item }">
                      <status-chip :color="item.is_valid ? 'success' : 'error'" :label="String(item.row_number)" />
                    </template>

                    <!-- Product Section -->
                    <template #[`item.product_id`]="{ item }">
                      <span class="text-caption">{{ item.data.product_id || '-' }}</span>
                    </template>
                    <template #[`item.product_name`]="{ item }">
                      <span :class="item.is_valid ? '' : 'text-error'" class="text-caption">
                        {{ item.data.product_name || '-' }}
                      </span>
                    </template>
                    <template #[`item.brand_name`]="{ item }">
                      <span class="text-caption">{{ item.data.brand_name || '-' }}</span>
                    </template>
                    <template #[`item.hsn_code`]="{ item }">
                      <span class="text-caption">{{ item.data.hsn_code || '-' }}</span>
                    </template>
                    <template #[`item.category`]="{ item }">
                      <span class="text-caption">{{ item.data.category || '-' }}</span>
                    </template>
                    <template #[`item.tags`]="{ item }">
                      <v-tooltip bottom max-width="300" v-if="item.data.tags">
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-caption text-truncate-cell">
                            {{ truncateText(item.data.tags, 20) }}
                          </span>
                        </template>
                        <span>{{ item.data.tags }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.description`]="{ item }">
                      <v-tooltip bottom max-width="300" v-if="item.data.description">
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-caption text-truncate-cell">
                            {{ truncateText(item.data.description, 25) }}
                          </span>
                        </template>
                        <span>{{ item.data.description }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.ingredient`]="{ item }">
                      <v-tooltip bottom max-width="300" v-if="item.data.ingredient">
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-caption text-truncate-cell">
                            {{ truncateText(item.data.ingredient, 20) }}
                          </span>
                        </template>
                        <span>{{ item.data.ingredient }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.benefits`]="{ item }">
                      <v-tooltip bottom max-width="300" v-if="item.data.benefits">
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-caption text-truncate-cell">
                            {{ truncateText(item.data.benefits, 20) }}
                          </span>
                        </template>
                        <span>{{ item.data.benefits }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.about`]="{ item }">
                      <v-tooltip bottom max-width="300" v-if="item.data.about">
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-caption text-truncate-cell">
                            {{ truncateText(item.data.about, 20) }}
                          </span>
                        </template>
                        <span>{{ item.data.about }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.precaution`]="{ item }">
                      <v-tooltip bottom max-width="300" v-if="item.data.precaution">
                        <template #activator="{ on, attrs }">
                          <span v-bind="attrs" v-on="on" class="text-caption text-truncate-cell">
                            {{ truncateText(item.data.precaution, 20) }}
                          </span>
                        </template>
                        <span>{{ item.data.precaution }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption">-</span>
                    </template>

                    <!-- Product Variant Section -->
                    <template #[`item.product_variant_name`]="{ item }">
                      <span class="text-caption">{{ item.data.product_variant_name || '-' }}</span>
                    </template>
                    <template #[`item.potency`]="{ item }">
                      <span class="text-caption">{{ item.data.potency || '-' }}</span>
                    </template>
                    <template #[`item.sku_id`]="{ item }">
                      <v-chip x-small outlined color="primary" v-if="item.data.sku_id">
                        {{ item.data.sku_id }}
                      </v-chip>
                      <span v-else class="text-caption grey--text">Auto</span>
                    </template>
                    <template #[`item.unit`]="{ item }">
                      <span class="text-caption">{{ item.data.unit || '-' }}</span>
                    </template>
                    <template #[`item.packing_type`]="{ item }">
                      <span class="text-caption">{{ item.data.packing_type || '-' }}</span>
                    </template>
                    <template #[`item.length`]="{ item }">
                      <span class="text-caption">{{ item.data.length || '-' }}</span>
                    </template>
                    <template #[`item.width`]="{ item }">
                      <span class="text-caption">{{ item.data.width || '-' }}</span>
                    </template>
                    <template #[`item.height`]="{ item }">
                      <span class="text-caption">{{ item.data.height || '-' }}</span>
                    </template>
                    <template #[`item.weight`]="{ item }">
                      <span class="text-caption">{{ item.data.weight || '-' }}</span>
                    </template>

                    <!-- Seller Section -->
                    <template #[`item.seller_code`]="{ item }">
                      <v-chip x-small outlined color="info" v-if="item.data.seller_code">
                        {{ item.data.seller_code }}
                      </v-chip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.position`]="{ item }">
                      <v-chip x-small :color="item.data.position ? 'purple' : 'grey lighten-2'" :dark="!!item.data.position" v-if="item.data.position">
                        {{ item.data.position }}
                      </v-chip>
                      <span v-else class="text-caption grey--text">All</span>
                    </template>
                    <template #[`item.maximum_retail_price`]="{ item }">
                      <span class="text-caption font-weight-medium" v-if="item.data.maximum_retail_price">
                        ₹{{ Number(item.data.maximum_retail_price).toFixed(2) }}
                      </span>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.seller_discount`]="{ item }">
                      <span class="text-caption">{{ item.data.seller_discount || '-' }}</span>
                    </template>
                    <template #[`item.ht_discount`]="{ item }">
                      <span class="text-caption">{{ item.data.ht_discount || '-' }}</span>
                    </template>
                    <template #[`item.consumer_discount`]="{ item }">
                      <span class="text-caption">{{ item.data.consumer_discount || '-' }}</span>
                    </template>
                    <template #[`item.doctor_discount`]="{ item }">
                      <span class="text-caption">{{ item.data.doctor_discount || '-' }}</span>
                    </template>
                    <template #[`item.expiry_date`]="{ item }">
                      <span class="text-caption">{{ formatExpiryDate(item.data.expiry_date) }}</span>
                    </template>
                    <template #[`item.stock`]="{ item }">
                      <v-chip x-small :color="getStockChipColor(item.data.stock)" dark v-if="item.data.stock !== undefined && item.data.stock !== null && item.data.stock !== ''">
                        {{ item.data.stock }}
                      </v-chip>
                      <span v-else class="text-caption">-</span>
                    </template>
                    <template #[`item.image_url_1`]="{ item }">
                      <v-tooltip bottom v-if="item.data.image_url_1">
                        <template #activator="{ on, attrs }">
                          <v-chip x-small color="success" dark v-bind="attrs" v-on="on">
                            <v-icon x-small left>mdi-image</v-icon>
                            Image
                          </v-chip>
                        </template>
                        <span>{{ item.data.image_url_1 }}</span>
                      </v-tooltip>
                      <v-chip x-small color="error" dark v-else>
                        <v-icon x-small left>mdi-image-off</v-icon>
                        Required
                      </v-chip>
                    </template>
                    <template #[`item.image_url_2`]="{ item }">
                      <v-tooltip bottom v-if="item.data.image_url_2">
                        <template #activator="{ on, attrs }">
                          <v-chip x-small color="info" outlined v-bind="attrs" v-on="on">
                            <v-icon x-small left>mdi-image</v-icon>
                            Image
                          </v-chip>
                        </template>
                        <span>{{ item.data.image_url_2 }}</span>
                      </v-tooltip>
                      <span v-else class="text-caption grey--text">-</span>
                    </template>
                  </v-data-table>
                </v-card>

                <!-- Validation Errors (if any) -->
                <v-card v-if="validationSummary.has_errors" outlined color="error">
                  <v-card-title class="pb-2 error white--text">
                    <v-icon color="white" class="mr-2">mdi-alert</v-icon>
                    Validation Errors
                    <v-spacer></v-spacer>
                    <v-btn
                      v-if="uploadId"
                      small
                      outlined
                      color="white"
                      @click="downloadErrorsExcel"
                      :loading="isDownloadingErrors"
                      class="ml-2"
                    >
                      <v-icon left small>mdi-download</v-icon>
                      Download Errors
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <v-expansion-panels>
                      <v-expansion-panel v-for="(error, index) in validationErrors.slice(0, 10)" :key="index">
                        <v-expansion-panel-header>
                          <strong>Row {{ error.row_number }}:</strong>
                          <span class="ml-2">{{ error.data.product_name || 'Unnamed Product' }}</span>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <v-list dense>
                            <v-list-item v-for="(messages, field) in error.errors" :key="field">
                              <v-list-item-icon>
                                <v-icon small color="error">mdi-alert-circle</v-icon>
                              </v-list-item-icon>
                              <v-list-item-content>
                                <v-list-item-title class="text-caption">
                                  <strong>{{ field }}:</strong> {{ Array.isArray(messages) ? messages.join(', ') : messages }}
                                </v-list-item-title>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                    <div v-if="validationErrors.length > 10" class="pa-3 text-center">
                      <span class="text-caption">
                        Showing first 10 errors. Total: {{ validationErrors.length }} errors.
                      </span>
                      <br>
                      <v-btn
                        v-if="uploadId"
                        x-small
                        color="error"
                        outlined
                        @click="downloadErrorsExcel"
                        :loading="isDownloadingErrors"
                        class="mt-2"
                      >
                        <v-icon left x-small>mdi-microsoft-excel</v-icon>
                        Download All {{ validationErrors.length }} Errors
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Action Buttons -->
                <div class="d-flex justify-space-between mt-4">
                  <v-btn
                    outlined
                    @click="currentStep = 1"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back to Upload
                  </v-btn>
                  <div>
                    <v-btn
                      v-if="validationSummary.has_errors && uploadId"
                      color="error"
                      outlined
                      @click="downloadErrorsExcel"
                      :loading="isDownloadingErrors"
                      class="mr-2"
                    >
                      <v-icon left>mdi-download</v-icon>
                      Download Errors
                    </v-btn>
                    <v-btn
                      v-if="validationSummary.has_errors"
                      color="warning"
                      @click="proceedWithErrors"
                      :disabled="validationSummary.valid_rows === 0"
                      class="mr-2"
                    >
                      <v-icon left>mdi-alert</v-icon>
                      Proceed with Valid Rows ({{ validationSummary.valid_rows }})
                    </v-btn>
                    <v-btn
                      v-else
                      color="success"
                      @click="currentStep = 3"
                    >
                      <v-icon left>mdi-check</v-icon>
                      Proceed to Confirmation
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </div>

            <!-- Section Divider -->
            <v-divider v-if="currentStep > 2" class="mx-4 my-2"></v-divider>

            <!-- Step 3: Confirm & Save -->
            <div v-show="currentStep === 3 && canConfirm">
              <v-card-text class="pa-4 pt-2">
                <v-alert
                  :type="validationSummary.has_errors ? 'warning' : 'success'"
                  outlined
                  class="mb-4"
                >
                  <div v-if="validationSummary.has_errors">
                    <strong>Partial Upload:</strong> 
                    {{ validationSummary.valid_rows }} out of {{ validationSummary.total_rows }} seller product variants will be processed.
                    {{ validationSummary.invalid_rows }} variants have validation errors and will be skipped.
                  </div>
                  <div v-else>
                    <strong>Full Upload:</strong>
                    All {{ validationSummary.total_rows }} seller product variants passed validation and will be processed.
                  </div>
                </v-alert>

                <v-card outlined class="mb-4">
                  <v-card-title class="pb-2">
                    <v-icon color="info" class="mr-2">mdi-information</v-icon>
                    What happens next?
                  </v-card-title>
                  <v-card-text>
                    <v-list dense>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-database</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Products and variants will be created/updated automatically
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-account-plus</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Sellers will be created if they don't exist (by phone number)
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-plus-box</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Stock will be incremented for existing variants
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-icon>
                          <v-icon small color="primary">mdi-history</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                          <v-list-item-title class="text-body-2">
                            Upload activity will be logged for future reference
                          </v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>

                <div class="d-flex justify-space-between">
                  <v-btn
                    outlined
                    @click="currentStep = 2"
                  >
                    <v-icon left>mdi-arrow-left</v-icon>
                    Back to Preview
                  </v-btn>
                  <v-btn
                    color="success"
                    :loading="isConfirming"
                    @click="confirmUpload"
                    class="px-6"
                  >
                    <v-icon left>mdi-check</v-icon>
                    Confirm & Process Variants
                  </v-btn>
                </div>
              </v-card-text>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600" persistent>
        <v-card>
          <v-card-title class="success white--text">
            <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
            Upload Completed Successfully!
          </v-card-title>
          <v-card-text class="pa-4">
            <div class="text-center">
              <v-icon size="64" color="success" class="mb-3">mdi-package-variant</v-icon>
              <h3 class="mb-3">Seller Product Variants Processed Successfully</h3>
              
              <v-row class="mb-3">
                <v-col cols="4">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h4 font-weight-bold text-success">{{ uploadResult.created_count }}</div>
                      <div class="text-caption">Created</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h4 font-weight-bold text-info">{{ uploadResult.updated_count }}</div>
                      <div class="text-caption">Updated</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="4">
                  <v-card outlined>
                    <v-card-text class="text-center pa-3">
                      <div class="text-h4 font-weight-bold text-error">{{ uploadResult.failed_count }}</div>
                      <div class="text-caption">Failed</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              
              <p class="text-body-2 grey--text">
                Your seller product variants have been successfully processed.
                Images have been downloaded and stored automatically.
                You can view them in the variants list or upload history.
              </p>
            </div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              outlined
              @click="uploadAnother"
            >
              Upload Another File
            </v-btn>
            <v-btn
              color="primary"
              @click="goToSellerProducts"
            >
              View Seller Products
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "SellerProductVariantUpload",
  data() {
    return {
      currentStep: 1,
      selectedFile: null,
      uploadNotes: '',
      isDownloadingTemplate: false,
      isUploading: false,
      isConfirming: false,
      isDownloadingErrors: false,
      
      // Preview data
      hasPreviewData: false,
      previewData: [],
      validationSummary: {
        total_rows: 0,
        total_file_rows: 0,
        valid_rows: 0,
        invalid_rows: 0,
        skipped_rows: 0,
        success_rate: 0,
        has_errors: false,
        has_skipped: false,
      },
      validationErrors: [],
      uploadId: null,
      
      // Master data for reference
      isLoadingMasters: false,
      categories: [],
      units: [],
      packingTypes: [],
      tags: [],
      positions: [],

      // Success dialog
      showSuccessDialog: false,
      uploadResult: {
        created_count: 0,
        updated_count: 0,
        failed_count: 0,
      },
      
      // Form validation
      fileRules: [
        v => !!v || 'Excel file is required',
        v => !v || v.size < 10485760 || 'File size should be less than 10MB',
        v => !v || ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'].includes(v.type) || 'File must be .xlsx or .xls format',
      ],
      fileErrors: [],
      
      // Table headers for preview - All columns from template
      previewHeaders: [
        // Row identifier
        { text: 'Row', value: 'row_number', width: '60px', sortable: false },
        // Product Section
        { text: 'Product ID', value: 'product_id', width: '90px', sortable: false },
        { text: 'Product Name', value: 'product_name', width: '160px', sortable: false },
        { text: 'Brand Name', value: 'brand_name', width: '130px', sortable: false },
        { text: 'HSN Code', value: 'hsn_code', width: '100px', sortable: false },
        { text: 'Category', value: 'category', width: '130px', sortable: false },
        { text: 'Tags', value: 'tags', width: '140px', sortable: false },
        { text: 'Description', value: 'description', width: '180px', sortable: false },
        { text: 'Ingredient', value: 'ingredient', width: '160px', sortable: false },
        { text: 'Benefits', value: 'benefits', width: '160px', sortable: false },
        { text: 'About', value: 'about', width: '160px', sortable: false },
        { text: 'Precaution', value: 'precaution', width: '160px', sortable: false },
        // Product Variant Section
        { text: 'Variant Name', value: 'product_variant_name', width: '160px', sortable: false },
        { text: 'Potency', value: 'potency', width: '90px', sortable: false },
        { text: 'SKU ID', value: 'sku_id', width: '120px', sortable: false },
        { text: 'Unit', value: 'unit', width: '90px', sortable: false },
        { text: 'Packing Type', value: 'packing_type', width: '110px', sortable: false },
        { text: 'Length', value: 'length', width: '80px', sortable: false },
        { text: 'Width', value: 'width', width: '80px', sortable: false },
        { text: 'Height', value: 'height', width: '80px', sortable: false },
        { text: 'Weight', value: 'weight', width: '80px', sortable: false },
        // Seller Section
        { text: 'Seller Code', value: 'seller_code', width: '110px', sortable: false },
        { text: 'Position', value: 'position', width: '90px', sortable: false },
        { text: 'MRP', value: 'maximum_retail_price', width: '100px', sortable: false },
        { text: 'Seller Disc.', value: 'seller_discount', width: '100px', sortable: false },
        { text: 'HT Disc.', value: 'ht_discount', width: '90px', sortable: false },
        { text: 'Consumer Disc.', value: 'consumer_discount', width: '110px', sortable: false },
        { text: 'Doctor Disc.', value: 'doctor_discount', width: '100px', sortable: false },
        { text: 'Expiry Date', value: 'expiry_date', width: '110px', sortable: false },
        { text: 'Stock', value: 'stock', width: '80px', sortable: false },
        { text: 'Image URL 1*', value: 'image_url_1', width: '120px', sortable: false },
        { text: 'Image URL 2', value: 'image_url_2', width: '120px', sortable: false },
      ],
    };
  },
  
  computed: {
    canConfirm() {
      return this.hasPreviewData && this.validationSummary.valid_rows > 0;
    },
  },
  
  mounted() {
    this.getMasterData();
  },
  
  methods: {
    async getMasterData() {
      this.isLoadingMasters = true;
      try {
        const response = await axios.get('/masters?context=products');

        if (response.data) {
          this.categories = response.data.categories || [];
          this.units = (response.data.units || []).map((unit) => ({
            id: unit.id,
            name: unit.description,
            description: unit.description
          }));
          this.packingTypes = (response.data.packing_types || []).map((packingType) => ({
            id: packingType.id,
            name: packingType.description,
            description: packingType.description
          }));
          this.tags = response.data.tags || [];
        }
      } catch (error) {
        console.error('Error fetching master data:', error);
        this.$store.dispatch('errors/setError', {
          message: 'Error fetching reference data',
        });
      } finally {
        this.isLoadingMasters = false;
      }

      try {
        const posRes = await axios.get('/positions?rowsPerPage=100');
        this.positions = (posRes.data.data || posRes.data || []).filter(
          p => p.name && p.name.toUpperCase() !== 'SELLER'
        );
      } catch (e) {
        console.error('Error fetching positions:', e);
      }
    },
    
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$toast.success(`Copied: ${text}`);
      } catch (err) {
        // Fallback for browsers that don't support clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.$toast.success(`Copied: ${text}`);
      }
    },
    
    async downloadTemplate() {
      this.isDownloadingTemplate = true;
      try {
        const response = await axios.get('/seller-product-variants/upload/template', {
          responseType: 'blob',
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `seller_product_variant_template_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$toast.success('Template downloaded successfully');
      } catch (error) {
        console.error('Error downloading template:', error);
        this.$store.dispatch('errors/setError', {
          message: 'Error downloading template',
        });
      } finally {
        this.isDownloadingTemplate = false;
      }
    },
    
    handleFileSelection() {
      this.fileErrors = [];
    },
    
    async uploadAndPreview() {
      if (!this.selectedFile) return;
      
      this.isUploading = true;
      this.fileErrors = [];
      
      try {
        const formData = new FormData();
        formData.append('file', this.selectedFile);
        if (this.uploadNotes) {
          formData.append('notes', this.uploadNotes);
        }
        
        const response = await axios.post('/seller-product-variants/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data.success) {
          this.uploadId = response.data.data.upload_id;
          this.validationSummary = response.data.data.validation_summary;
          this.previewData = response.data.data.preview_data;
          this.validationErrors = response.data.data.errors;
          this.hasPreviewData = true;
          this.currentStep = 2;
          
          this.$toast.success('File uploaded and validated successfully');
        } else {
          this.$store.dispatch('errors/setError', {
            message: response.data.message,
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        if (error.response && error.response.status === 422) {
          this.fileErrors = error.response.data.errors.file || [];
        } else {
          this.$store.dispatch('errors/setError', {
            message: 'Error uploading file',
          });
        }
      } finally {
        this.isUploading = false;
      }
    },
    
    proceedWithErrors() {
      this.currentStep = 3;
    },
    
    async confirmUpload() {
      if (!this.uploadId) return;
      
      this.isConfirming = true;
      
      try {
        const response = await axios.post(`/seller-product-variants/upload/${this.uploadId}/confirm`);
        
        if (response.data.success) {
          const summary = response.data.data.summary;
          this.uploadResult.created_count = summary.created || 0;
          this.uploadResult.updated_count = summary.updated || 0;
          this.uploadResult.failed_count = summary.failed || 0;
          this.showSuccessDialog = true;
        } else {
          this.$store.dispatch('errors/setError', {
            message: response.data.message,
          });
        }
      } catch (error) {
        console.error('Error confirming upload:', error);
        this.$store.dispatch('errors/setError', {
          message: 'Error confirming upload',
        });
      } finally {
        this.isConfirming = false;
      }
    },
    
    uploadAnother() {
      this.showSuccessDialog = false;
      this.resetForm();
    },
    
    goToSellerProducts() {
      this.showSuccessDialog = false;
      this.$router.push('/seller-products');
    },
    
    getSellerDisplayName(data) {
      const firstName = data.first_name || '';
      const lastName = data.last_name || '';
      const phone = data.phone || '';
      
      const fullName = `${firstName} ${lastName}`.trim();
      return fullName || phone || 'N/A';
    },
    
    getStockChipColor(stock) {
      const stockNum = Number(stock) || 0;
      if (stockNum === 0) return 'error';
      if (stockNum <= 10) return 'warning';
      if (stockNum <= 50) return 'info';
      return 'success';
    },

    truncateText(text, maxLength = 30) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    formatExpiryDate(date) {
      if (!date) return '-';
      // Handle YYYY-MM-DD format
      if (date.includes('-')) {
        const parts = date.split('-');
        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
      }
      // Handle DD/MM/YYYY format (already formatted)
      if (date.includes('/')) {
        return date;
      }
      return date;
    },
    
    async downloadErrorsExcel() {
      if (!this.uploadId) {
        this.$store.dispatch('errors/setError', {
          message: 'Upload ID not available for downloading errors',
        });
        return;
      }
      
      this.isDownloadingErrors = true;
      try {
        const response = await axios.get(`/seller-product-variants/upload/${this.uploadId}/errors`, {
          responseType: 'blob',
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `seller_product_variant_upload_errors_${this.uploadId}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$toast.success('Error report downloaded successfully');
      } catch (error) {
        console.error('Error downloading error report:', error);
        this.$store.dispatch('errors/setError', {
          message: 'Error downloading error report',
        });
      } finally {
        this.isDownloadingErrors = false;
      }
    },
    
    resetForm() {
      this.currentStep = 1;
      this.selectedFile = null;
      this.uploadNotes = '';
      this.hasPreviewData = false;
      this.previewData = [];
      this.validationSummary = {
        total_rows: 0,
        valid_rows: 0,
        invalid_rows: 0,
        success_rate: 0,
        has_errors: false,
      };
      this.validationErrors = [];
      this.uploadId = null;
      this.fileErrors = [];
      this.uploadResult = {
        created_count: 0,
        updated_count: 0,
        failed_count: 0,
      };
      this.isDownloadingErrors = false;
    },
  },
};
</script>

<style scoped>
.v-stepper >>> .v-stepper__step--active .v-stepper__label {
  text-shadow: none;
}

.v-stepper >>> .v-stepper__step--complete .v-icon {
  color: #4CAF50 !important;
}

.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}

.text-error {
  color: #F44336 !important;
}

.text-success {
  color: #4CAF50 !important;
}

.text-warning {
  color: #FF9800 !important;
}

.chip-clickable {
  cursor: pointer !important;
  transition: transform 0.2s ease !important;
}

.chip-clickable:hover {
  transform: scale(1.05) !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important;
}

/* Preview table styles for horizontal scroll */
.preview-table >>> .v-data-table__wrapper {
  overflow-x: auto !important;
  max-width: 100%;
}

.preview-table >>> table {
  min-width: 3800px;
}

.preview-table >>> th {
  white-space: nowrap;
  font-size: 11px !important;
  font-weight: 600 !important;
  background-color: #f5f5f5 !important;
  position: sticky;
  top: 0;
  z-index: 1;
}

.preview-table >>> td {
  white-space: nowrap;
  font-size: 12px !important;
  padding: 8px 12px !important;
}

.preview-table >>> .v-chip {
  font-size: 10px !important;
}

.text-truncate-cell {
  max-width: 150px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
</style>